'use client'

import { memo, useEffect, useState, useRef } from 'react'

import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
interface PaymentDeadlineProps {
  time: string // seconds
  setIsTimeoutModalOpen?: any
}

const PaymentDeadlinePc = ({ time, setIsTimeoutModalOpen }: PaymentDeadlineProps) => {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const parsedTime = parseInt(time, 10)
    return isNaN(parsedTime) ? 15 * 60 : parsedTime // Default to 15 minutes if invalid
  })
  const [isTimeout, setIsTimeout] = useState(false)
  const hasTimedOut = useRef(false)

  // Reset timeout state when time prop changes
  useEffect(() => {
    const parsedTime = parseInt(time, 10)
    if (!isNaN(parsedTime)) {
      setSecondsLeft(parsedTime)
      if (parsedTime > 0) {
        hasTimedOut.current = false
        setIsTimeout(false)
      }
    }
  }, [time])

  useEffect(() => {
    // Don't start interval if we're already at 0
    if (secondsLeft <= 0) {
      if (!hasTimedOut.current) {
        hasTimedOut.current = true
        setIsTimeout(true)
      }
      return
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 0) {
          if (!hasTimedOut.current) {
            hasTimedOut.current = true
            setIsTimeout(true)
          }
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [secondsLeft])

  useEffect(() => {
    if (isTimeout) {
      setIsTimeoutModalOpen(true)
    }
  }, [isTimeout, setIsTimeoutModalOpen])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  return (
    <>
      {/* pc */}
      <div className="hidden bg-[#F8F4F0] md:block md:rounded-[6px] md:bg-primary-500 md:p-2">
        <span className="text-sm font-medium text-[#2D2E32] md:text-white">
          <span className="leading-4">{t('payment.deadlineExpire')}</span>
          <b className="font-bold">{formatTime(secondsLeft)}</b>
        </span>
      </div>
    </>
  )
}

function arePropsEqual(prevProps: PaymentDeadlineProps, nextProps: PaymentDeadlineProps): boolean {
  return (
    prevProps.time === nextProps.time &&
    prevProps.setIsTimeoutModalOpen === nextProps.setIsTimeoutModalOpen
  )
}

export default memo(PaymentDeadlinePc, arePropsEqual)
